import type { Service, ServiceLength } from '../../api/client'
import type { Package, PackageInput } from '../../types/app'
import { parsePrice, parseWholeNumber } from '../../catalog/format'
import { discountFromPrice, priceFromDiscount, savingPence } from '../../packages/pricing'

export interface PackageForm {
  name: string
  description: string
  sessions: string
  price: string
  discount: string
  lengthIds: string[]
  // validity is a count of validityUnit: months by default (Jessica thinks
  // of a course as "valid for 6 months"); a package made in days stays in days.
  validity: string
  validityUnit: ValidityUnit
  // sellOnline puts it on her booking page; a new package is.
  sellOnline: boolean
}

export type ValidityUnit = 'months' | 'days'

export function emptyForm(): PackageForm {
  return {
    name: '',
    description: '',
    sessions: '',
    price: '',
    discount: '',
    lengthIds: [],
    validity: '',
    validityUnit: 'months',
    sellOnline: true,
  }
}

// priceField formats pence the way the price field edits it, always with two
// decimal places so "£252" and "£252.50" line up as she types. It duplicates
// catalog/format.ts's priceInput (which omits ".00") deliberately: an
// editable price field needs a stable decimal-place count, a formatted
// display label doesn't.
function priceField(pence: number): string {
  return (pence / 100).toFixed(2)
}

// toInt reads a whole-number field: "12.5" or "1e6" is null, never 12 or 1.
function toInt(value: string): number | null {
  return parseWholeNumber(value)
}

// allLengths flattens the menu into its lengths.
function allLengths(services: Service[]): ServiceLength[] {
  return services.flatMap((s) => s.lengths)
}

// liveLengthIds keeps the ticked ids that are still a length on the menu, in
// the order they were ticked. A length she has since removed is dropped.
export function liveLengthIds(services: Service[], ticked: string[]): string[] {
  const live = new Set(allLengths(services).map((l) => l.id))
  return ticked.filter((id) => live.has(id))
}

// groupLengths is the checklist: each service with its live lengths, shortest
// first, and whether each is ticked. A ticked id that is no longer a length
// does not appear. A one-length service has one entry, which the sheet shows
// as a single checkbox named for the service.
export function groupLengths(
  services: Service[],
  ticked: string[],
): { service: Service; lengths: { length: ServiceLength; checked: boolean }[] }[] {
  return services.map((service) => ({
    service,
    lengths: service.lengths.map((length) => ({ length, checked: ticked.includes(length.id) })),
  }))
}

// singleLengthUnit returns the unit price of the one ticked length, or null
// when zero or several are ticked and there is no single baseline.
export function singleLengthUnit(lengthIds: string[], services: Service[]): number | null {
  if (lengthIds.length !== 1) {
    return null
  }
  return allLengths(services).find((l) => l.id === lengthIds[0])?.price_pence ?? null
}

// savingRange reports the saving a package represents across each ticked
// length's own price, for when several are ticked and there is no single
// baseline to show one number against.
export function savingRange(
  sessions: number,
  lengthIds: string[],
  services: Service[],
  pricePence: number,
): { min: number; max: number } | null {
  const lengths = allLengths(services)
  const units = lengthIds
    .map((lengthId) => lengths.find((l) => l.id === lengthId)?.price_pence)
    .filter((unit): unit is number => unit != null)
  if (units.length === 0) {
    return null
  }
  const savings = units.map((unit) => savingPence(sessions, unit, pricePence))
  return { min: Math.min(...savings), max: Math.max(...savings) }
}

// applyDiscount is called as she types a percentage: it fills in the price
// that percentage represents, using the single selected service's price as
// the baseline. If sessions, the discount, or the baseline aren't there to
// compute from, only the discount field itself changes.
export function applyDiscount(form: PackageForm, discount: string, unitPence: number): PackageForm {
  const next = { ...form, discount }
  const sessions = toInt(form.sessions)
  const percent = parseWholeNumber(discount)
  if (sessions === null || sessions <= 0 || percent === null || unitPence <= 0) {
    return next
  }
  next.price = priceField(priceFromDiscount(sessions, unitPence, percent))
  return next
}

// applyPrice is called as she types a price: it fills in the discount that
// price represents against the single selected service's price. discount_percent
// is stored as a whole integer, so most prices aren't *exactly* some whole
// percent off — only when priceFromDiscount(percent) lands back on the exact
// price she typed is that percent shown; re-applying it is then a no-op.
// Otherwise the discount is cleared rather than shown as a rounded figure
// that would silently move her price the moment it was reapplied.
export function applyPrice(form: PackageForm, price: string, unitPence: number): PackageForm {
  const next = { ...form, price }
  const sessions = toInt(form.sessions)
  const pricePence = parsePrice(price)
  if (sessions === null || sessions <= 0 || pricePence === null) {
    return next
  }
  const percent = discountFromPrice(sessions, unitPence, pricePence)
  const exact = percent !== null && priceFromDiscount(sessions, unitPence, percent) === pricePence
  next.discount = exact ? String(percent) : ''
  return next
}

// priceChangeNote explains when a saved package's stored discount no longer
// matches what its stored price represents, because the service's price has
// moved since. The price stays what was charged; the note just says so.
export function priceChangeNote(pkg: Package, services: Service[]): string | null {
  const unit = singleLengthUnit(pkg.service_length_ids, services)
  if (unit === null) {
    return null
  }
  const current = discountFromPrice(pkg.session_count, unit, pkg.price_pence)
  if (current === null || current === pkg.discount_percent) {
    return null
  }
  return `The treatment price changed, so this is now ${current}% off. The package price has not changed.`
}

// toPackageForm shows the real current discount for the package's single
// service, not the possibly stale stored figure — see priceChangeNote. Like
// applyPrice, it only shows a percent when re-applying it reproduces the
// stored price exactly: discount_percent is a whole integer, so most prices
// aren't exactly some whole percent off, and showing a rounded figure would
// silently move the price the moment it was reapplied.
export function toPackageForm(pkg: Package | null, services: Service[]): PackageForm {
  if (!pkg) {
    return emptyForm()
  }
  const unit = singleLengthUnit(pkg.service_length_ids, services)
  let discount: number | null
  if (unit === null) {
    discount = pkg.discount_percent
  } else {
    const current = discountFromPrice(pkg.session_count, unit, pkg.price_pence)
    const exact = current !== null && priceFromDiscount(pkg.session_count, unit, current) === pkg.price_pence
    discount = exact ? current : null
  }
  return {
    name: pkg.name,
    description: pkg.description ?? '',
    sessions: String(pkg.session_count),
    price: priceField(pkg.price_pence),
    discount: discount === null ? '' : String(discount),
    lengthIds: liveLengthIds(services, pkg.service_length_ids),
    sellOnline: pkg.sellable_online ?? true,
    ...(pkg.validity_months
      ? { validity: String(pkg.validity_months), validityUnit: 'months' as const }
      : pkg.validity_days
        ? { validity: String(pkg.validity_days), validityUnit: 'days' as const }
        : { validity: '', validityUnit: 'months' as const }),
  }
}

// toPackageInput returns the request body, or the errors it can spot without
// asking the API. The API still checks every rule.
export function toPackageInput(
  form: PackageForm,
  services?: Service[],
): { input: PackageInput } | { errors: Record<string, string> } {
  const errors: Record<string, string> = {}
  const lengthIds = services ? liveLengthIds(services, form.lengthIds) : form.lengthIds

  const name = form.name.trim()
  if (name === '') {
    errors.name = 'Enter a name.'
  }

  const sessions = toInt(form.sessions)
  if (sessions === null || sessions <= 0) {
    errors.session_count = 'Enter the number of sessions.'
  }

  const price = parsePrice(form.price)
  if (price === null) {
    errors.price_pence = 'Enter a price like 250 or 252.50.'
  }

  if (lengthIds.length === 0) {
    errors.service_ids = 'Choose at least one treatment.'
  }

  let validity: number | null = null
  if (form.validity.trim() !== '') {
    validity = toInt(form.validity)
    if (validity === null || validity <= 0) {
      errors[form.validityUnit === 'months' ? 'validity_months' : 'validity_days'] =
        `Enter the number of ${form.validityUnit}, or leave it blank.`
    }
  }

  const discountText = lengthIds.length === 1 ? form.discount.trim() : ''
  const discount = discountText === '' ? null : parseWholeNumber(discountText)
  if (discountText !== '' && discount === null) {
    errors.discount_percent = 'Enter a whole percentage, like 10, or leave it blank.'
  }

  if (Object.keys(errors).length > 0 || sessions === null || price === null) {
    return { errors }
  }

  const description = form.description.trim()

  return {
    input: {
      name,
      description: description === '' ? null : description,
      session_count: sessions,
      price_pence: price,
      discount_percent: discount,
      validity_days: form.validityUnit === 'days' ? validity : null,
      validity_months: form.validityUnit === 'months' ? validity : null,
      service_length_ids: lengthIds,
      sellable_online: form.sellOnline,
    },
  }
}
